import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from 'react'

/** The project name and app URL keep at least this much room before top-bar actions start collapsing. */
const MIN_IDENTITY_PX = 180
const GAP_PX = 4

/**
 * Moves top-bar actions into an overflow menu, `keys[0]` first, whenever the project identity (title + URL)
 * would otherwise be squeezed below MIN_IDENTITY_PX (or its full width, if shorter). Each action marks its
 * element(s) with `data-overflow-key`; an action that shrinks instead of disappearing (a compact variant)
 * keeps its key on the smaller element, and only the space it actually gave back counts when restoring.
 */
export function useTopbarOverflow<K extends string>(
  keys: readonly K[],
  headerRef: RefObject<HTMLElement | null>,
  identityRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): ReadonlySet<K> {
  const [level, setLevel] = useState(0)
  const levelRef = useRef(0)
  levelRef.current = Math.min(level, keys.length)
  // Width each collapsed action occupied when it was still full size, indexed by collapse order.
  const freed = useRef<number[]>([])
  const keysSig = keys.join('|')

  const widthOf = useCallback((key: K) => {
    const els = headerRef.current?.querySelectorAll<HTMLElement>(`[data-overflow-key="${key}"]`) ?? []
    return [...els].reduce((sum, el) => sum + el.getBoundingClientRect().width + GAP_PX, 0)
  }, [headerRef])

  const check = useCallback(() => {
    const identity = identityRef.current
    if (!enabled || !identity) return
    const width = identity.getBoundingClientRect().width
    const title = identity.querySelector<HTMLElement>('.chat-topbar__title')?.scrollWidth ?? 0
    const urlText = identity.querySelector<HTMLElement>('.chat-topbar__url-text')
    const url = urlText ? urlText.scrollWidth + 17 : 0
    const needed = Math.min(Math.max(title, url), MIN_IDENTITY_PX)
    const current = levelRef.current
    if (width < needed - 1 && current < keys.length) {
      freed.current[current] = widthOf(keys[current]!)
      setLevel(current + 1)
    } else if (current > 0) {
      const last = keys[current - 1]!
      const regained = (freed.current[current - 1] ?? Infinity) - widthOf(last)
      // Room to spare = the empty stretch between the name and the action cluster (keeping the regions'
      // padding) plus however much the name is wider than it needs to be.
      const cluster = headerRef.current?.querySelector<HTMLElement>('.chat-topbar__right')
      const gap = cluster ? cluster.getBoundingClientRect().left - identity.getBoundingClientRect().right - 16 : 0
      if (Math.max(gap, 0) + width - needed >= regained + GAP_PX) setLevel(current - 1)
    }
    // keysSig stands in for `keys`, which callers rebuild each render.
  }, [enabled, keysSig, identityRef, widthOf])

  useLayoutEffect(() => { check() }, [check, level])

  // The header and identity mount after the first render on a cold load (and remount across layouts), so
  // re-attach whenever the observed elements change rather than only when the effect's deps do.
  const observer = useRef<{ ro: ResizeObserver; els: (HTMLElement | null)[] } | null>(null)
  const checkRef = useRef(check)
  checkRef.current = check
  useLayoutEffect(() => {
    const els = enabled ? [headerRef.current, identityRef.current] : []
    const prev = observer.current
    if (prev && prev.els.length === els.length && prev.els.every((el, i) => el === els[i])) return
    prev?.ro.disconnect()
    observer.current = null
    if (!els.length || !els.every(Boolean)) return
    const ro = new ResizeObserver(() => checkRef.current())
    for (const el of els) ro.observe(el!)
    observer.current = { ro, els }
  })
  useLayoutEffect(() => () => {
    observer.current?.ro.disconnect()
    observer.current = null
  }, [])

  return new Set(enabled ? keys.slice(0, levelRef.current) : [])
}
